/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import {
    Chip,
    Container,
    Grid,
    Paper,
    Typography,
    Button,
    useMediaQuery,
    useTheme,
    CircularProgress,
    Snackbar,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { Alert } from '@mui/lab';
import { ChevronLeft } from '@mui/icons-material';
import { Prompt } from 'react-router-dom';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import { useDebouncedCallback } from 'use-debounce';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { WithContexts } from '../../contexts/withContexts';
import ExpectedPayDateCard from '../../components/ContentComponents/Timesheets/ExpectedPayDateCard';
import { COLORS } from '../../utils/Application_Constants';
import TimesheetAccordion from '../../components/ContentComponents/Timesheets/TimesheetAccordion';
import { getTimesheetById, saveTimesheet } from '../../services/TimeAndPayService';
import TimesheetStatusEnum, {
    TimesheetStatusEnumApi,
} from '../../components/ContentComponents/Timesheets/Status.enum';
import splitEntriesByBreaks from '../../Shared/Components/SplitEntriesByBreaks';
import { formatErrorMessage } from '../../Shared/Utils/ErrorMessage';
import SalesforceBookingLink from '../../Shared/Components/SalesforceBookingLink';
import RequiredQuestionDialog from '../../components/GlobalComponents/Dialog/RequiredQuestionDialog';

const styles = () => ({
    root: {
        backgroundColor: COLORS.LT_MIDNIGHTBG1,
        minHeight: '200px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    ContainerRoot: {
        paddingTop: '2.5rem',
        paddingBottom: '5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    },
    PaperRoot: {
        padding: '1.5rem',
        display: 'flex',
        gap: '24px',
        flexDirection: 'column',
    },
    containedSuccess: {
        backgroundColor: COLORS.LT_EMERALD,
        '&:hover': {
            backgroundColor: COLORS.LT_EMERALD75,
        },
        color: 'white',
        paddingLeft: '48px',
        paddingRight: '48px',
    },
    approveButton: {
        backgroundColor: COLORS.LT_EMERALD,
        '&:hover': {
            backgroundColor: COLORS.LT_EMERALD75,
        },
        color: 'white',
    },
    rejectButton: {
        backgroundColor: COLORS.LT_PEPPER10,
        color: COLORS.LT_PEPPER,
        '&:hover': {
            backgroundColor: COLORS.LT_PEPPER25,
        },
    },
    overline1: {
        fontWeight: '600',
    },
    overline2: {
        fontSize: '10px',
        fontWeight: '600',
        lineHeight: '18px',
        color: COLORS.LT_MIDNIGHT50,
    },
    body1: {
        lineHeight: '20px',
    },
    warningIcon: {
        marginRight: '35px',
    },
    warningGridWrapper: {
        background: COLORS.LT_SUNFLOWER25,
        padding: '25px 15px',
    },
    bookingName: {
        fontStyle: 'italic',
        color: COLORS.LT_MIDNIGHT50,
    },
    chipRoot: {
        textAlign: 'center',
        width: 'fit-content',
    },
    chipSubmitted: {
        backgroundColor: COLORS.GLITTER,
        color: COLORS.RICH_ELECTRIC_BLUE,
        borderColor: COLORS.RICH_ELECTRIC_BLUE,
    },
    chipReSubmitted: {
        backgroundColor: COLORS.GLITTER,
        color: COLORS.RICH_ELECTRIC_BLUE,
        borderColor: COLORS.RICH_ELECTRIC_BLUE,
    },
    chipApproved: {
        backgroundColor: COLORS.AQUA_SPRING,
        color: COLORS.KELLY_GREEN,
        borderColor: COLORS.KELLY_GREEN,
    },
    chipReApproved: {
        backgroundColor: COLORS.AQUA_SPRING,
        color: COLORS.KELLY_GREEN,
        borderColor: COLORS.KELLY_GREEN,
    },
    chipRejected: {
        backgroundColor: COLORS.LT_PEPPER10,
        color: COLORS.LT_PEPPER,
        borderColor: COLORS.LT_PEPPER,
    },
    chipPaid: {
        color: 'white',
        background: COLORS.LT_EMERALD,
    },
});

function TimesheetReviewClinician(props) {
    const { classes, t, UserContext, setRefreshNotifications, history } = props;
    const { ltoeWebTimesheetEnableExpectedPayDate } = useFlags();
    const [timesheet, setTimesheet] = useState(null);
    const [viewTimesheet, setViewTimesheet] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [alert, setAlert] = useState();
    const [timesheetHasBeenSubmitted, setTimesheetHasBeenSubmitted] = useState(false);
    const [showUnsubmittedWarning, setShowUnsubmittedWarning] = useState(false);
    const [nextLocation, setNextLocation] = useState(null);
    const bypassPromptRef = useRef(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'), {
        defaultMatches: true,
    });
    const { id } = useParams();
    const locationsToBeSubmitted = [];

    const { externalId } = UserContext;

    if (timesheet?.timesheetReviews) {
        for (let i = 0; i < timesheet.timesheetReviews.length; i++) {
            if (
                timesheet.timesheetReviews[i].status !== TimesheetStatusEnumApi.Submitted &&
                timesheet.timesheetReviews[i].status !== TimesheetStatusEnumApi.Approved &&
                timesheet.timesheetReviews[i].status !== TimesheetStatusEnumApi.Paid
            ) {
                const { locationId } = timesheet.timesheetReviews[i];
                locationsToBeSubmitted.push(
                    timesheet.booking?.locations?.filter(
                        (location) => location.id === locationId
                    )[0]?.displayName
                );
            }
        }
    }

    const convertUTCtoTime = (utcString, timeZoneBooking) => {
        let utcDateTime = utcString;
        if (utcString !== null) {
            utcDateTime = moment(utcString).tz(timeZoneBooking).format('HH:mm');
        }
        return utcDateTime;
    };

    const mapEntryBreakTimesToLocationTimezone = (entry, timeZoneBooking) => {
        const newEntry = entry;
        if (!newEntry.timesheetBreaks) return;

        newEntry.timesheetBreaks = newEntry.timesheetBreaks.sort((a, b) =>
            a.startTime.localeCompare(b.startTime)
        );

        newEntry.timesheetBreaks.map((timesheetBreak) => {
            const newTimesheetBreak = timesheetBreak;
            newTimesheetBreak.startTime = convertUTCtoTime(
                newTimesheetBreak.startTime,
                timeZoneBooking
            );
            newTimesheetBreak.endTime = convertUTCtoTime(
                newTimesheetBreak.endTime,
                timeZoneBooking
            );
            return newTimesheetBreak;
        });

        return newEntry.timesheetBreaks;
    };

    const mapTimeToLocationTimezone = (existingTimesheet) => {
        existingTimesheet.timesheetEntries.map((timesheetEntry) => {
            const updatedEntry = timesheetEntry;
            const timeZoneBooking =
                existingTimesheet?.booking?.locations?.find(
                    (location) => location.id === updatedEntry?.locationId
                )?.timeZone || moment.tz.guess();
            const { startTime } = updatedEntry;
            const { endTime } = updatedEntry;
            updatedEntry.startTime = convertUTCtoTime(startTime, timeZoneBooking);
            updatedEntry.endTime = convertUTCtoTime(endTime, timeZoneBooking);
            updatedEntry.transactionDate = moment(updatedEntry.transactionDate)
                .utc()
                .format('YYYY-MM-DDTHH:mm:ss');
            updatedEntry.timesheetBreaks = mapEntryBreakTimesToLocationTimezone(
                updatedEntry,
                timeZoneBooking
            );
            return updatedEntry;
        });
        existingTimesheet.mileageEntries.map((mileageEntry) => {
            const updatedMileage = mileageEntry;
            updatedMileage.transactionDate = moment(updatedMileage.transactionDate)
                .utc()
                .format('YYYY-MM-DDTHH:mm:ss');
            return updatedMileage;
        });
        return existingTimesheet;
    };

    useEffect(() => {
        const fetchTimesheetById = async () => {
            try {
                const timesheetDetails = await getTimesheetById(externalId, id, null);
                const viewTimesheetDetails = cloneDeep(timesheetDetails);
                const updatedTimesheet = mapTimeToLocationTimezone(viewTimesheetDetails);
                setViewTimesheet(updatedTimesheet);
                setTimesheet(timesheetDetails);

                const hasBeenSubmitted =
                    timesheetDetails?.timesheetReviews?.some(
                        (review) =>
                            review.status === TimesheetStatusEnumApi.Submitted ||
                            review.status === TimesheetStatusEnumApi.Approved ||
                            review.status === TimesheetStatusEnumApi.Paid
                    ) || false;
                setTimesheetHasBeenSubmitted(hasBeenSubmitted);

                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
                let message = formatErrorMessage(error);
                if (message === 'An error has occurred.')
                    message = 'Timesheet failed to load. Please try again.';
                setAlert({
                    message,
                    type: 'error',
                });
                console.error(message);
            }
        };
        fetchTimesheetById();
    }, []);

    useEffect(() => {
        const onBeforeUnload = (e) => {
            if (!isLoading && !timesheetHasBeenSubmitted) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
        };
    }, [isLoading, timesheetHasBeenSubmitted]);

    const handlePrompt = (location, navAction) => {
        if (bypassPromptRef.current) {
            return true;
        }

        if (timesheetHasBeenSubmitted || /\/edit(?:\/|$)/.test(location.pathname)) {
            return true;
        }

        setNextLocation({ location, navAction });
        setShowUnsubmittedWarning(true);
        return false;
    };

    const closeUnsubmittedWarning = (action) => {
        if (action === 'Stay') {
            setShowUnsubmittedWarning(false);
            setNextLocation(null);
        } else if (action === 'Leave') {
            setShowUnsubmittedWarning(false);
            if (nextLocation) {
                // Prompt's `when` is still active at this point, so navigating below would
                // otherwise re-trigger handlePrompt and block its own bypass navigation.
                bypassPromptRef.current = true;
                if (nextLocation.navAction === 'POP') {
                    // A back-button press already moved the browser's history pointer;
                    // completing it with goBack() keeps the stack shape intact. Pushing
                    // nextLocation instead would add a duplicate entry, so a second Back
                    // press would return here instead of continuing further back.
                    history.goBack();
                } else {
                    history.push(nextLocation.location);
                }
                bypassPromptRef.current = false;
            }
            setNextLocation(null);
        }
    };

    const formatPeriod = (weekEnding) => {
        const endDate = moment(weekEnding).format('MMM D, YYYY');
        const startDate = moment(weekEnding).subtract(6, 'days').format('MMM D');
        return `Week of ${startDate} - ${endDate}`;
    };

    const onSubmit = async () => {
        setIsLoading(true);
        const path = `/timesheet/${timesheet.id}/submit`;
        const newTimesheet = timesheet;
        newTimesheet.status = TimesheetStatusEnumApi.Submitted;
        newTimesheet.timesheetEntries = newTimesheet.timesheetEntries.filter(
            (entry) => entry.locationId && entry.rateTypeId
        );
        newTimesheet.mileageEntries = newTimesheet.mileageEntries.filter(
            (entry) => entry.locationId
        );
        try {
            await saveTimesheet(newTimesheet, timesheet.id);

            if (setRefreshNotifications) {
                setRefreshNotifications();
            }

            setTimesheetHasBeenSubmitted(true);
            // The leave-warning popup is still turned on at this point, so without this
            // line the push below would trigger it again instead of leaving the page.
            bypassPromptRef.current = true;
            history.push({
                pathname: path,
                state: { timesheetPreviouslySubmitted: timesheetHasBeenSubmitted },
            });
            bypassPromptRef.current = false;
            setIsLoading(false);
        } catch (error) {
            let message = formatErrorMessage(error);
            if (message === 'An error has occurred.')
                message = 'Timesheet has failed to be submitted. Please try again.';
            setAlert({
                message,
                type: 'error',
            });
            console.error(message);
            setIsLoading(false);
        }
        window.Appcues.track('Submit Timesheet For Processing (Submit)', {
            key: 'Click the Submit button',
            description: 'A user has submitted their timesheet for proessing',
            bookingName: newTimesheet.BookingName,
            weekEnding: newTimesheet.endDate,
        });
    };

    const debouncedOnSubmit = useDebouncedCallback(
        () => {
            onSubmit();
        },
        5000,
        { leading: true }
    );

    const formatListToCommaSeparated = (locations) => {
        const { length } = locations;

        if (length === 0) {
            return '';
        }
        if (length === 1) {
            return locations[0];
        }
        if (length === 2) {
            return `${locations[0]} and ${locations[1]}`;
        }
        let result = locations.slice(0, -1).join(', ');
        result += `, and ${locations[length - 1]}`;
        return result;
    };

    const renderTimesheetPanels = (bookingLocation, index) => {
        const timesheetEntries = viewTimesheet.timesheetEntries
            .filter((entry) => entry.locationId === bookingLocation.id)
            .sort((a, b) => new Date(a.transactionDate) - new Date(b.transactionDate));

        // Group timesheetEntries by relatedEntryId or form individual groups for entries without relatedEntryId
        const groups = {};
        timesheetEntries.forEach((entry, teIndex) => {
            if (entry.id === 0) {
                // eslint-disable-next-line no-param-reassign
                entry.id = -(teIndex + 1);
            }
            const { relatedEntryId } = entry;
            if (relatedEntryId) {
                if (!groups[relatedEntryId]) {
                    groups[relatedEntryId] = [];
                }
                groups[relatedEntryId].push(entry);
            } else {
                // Form individual groups for entries without relatedEntryId
                groups[entry.id] = [entry];
            }
        });

        const sortEntriesWithinGroup = (group) =>
            group.sort((a, b) => {
                const dateA = new Date(a.transactionDate);
                const dateB = new Date(b.transactionDate);
                if (dateA.getTime() !== dateB.getTime()) {
                    return dateA - dateB;
                }
                // Sort by start time if it exists
                if (a.startTime && b.startTime) {
                    return a.startTime.localeCompare(b.startTime);
                }
                // If start time doesn't exist, maintain the existing order
                return 0;
            });

        // sort groups by the transactionDate of their first entry
        const sortGroupsByTransactionDate = (transactionGroups) =>
            Object.values(transactionGroups).sort((a, b) => {
                const dateA = new Date(a[0].transactionDate);
                const dateB = new Date(b[0].transactionDate);
                return dateA - dateB;
            });

        // Sort groups by transactionDate
        const sortedGroups = sortGroupsByTransactionDate(groups);

        // Sort entries within each group
        sortedGroups.forEach((group) => {
            sortEntriesWithinGroup(group);
        });

        // Flatten sortedGroups into an array while keeping the order of groups
        let sortedEntries = sortedGroups.flatMap((group) => group);
        sortedEntries = splitEntriesByBreaks(sortedEntries);

        const mileageEntries = viewTimesheet.mileageEntries.filter(
            (entry) => entry.locationId === bookingLocation.id
        );

        return (
            <TimesheetAccordion
                key={bookingLocation.id}
                timesheetEntries={sortedEntries}
                mileageEntries={mileageEntries}
                timesheet={viewTimesheet}
                bookingLocation={bookingLocation}
                defaultExpanded={false}
                index={index}
            />
        );
    };

    const handleAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlert(null);
    };

    const renderSnackbar = () => (
        <Snackbar
            open={Boolean(alert)}
            autoHideDuration={6000}
            onClose={handleAlertClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Alert onClose={handleAlertClose} severity={alert?.type}>
                {alert?.message}
            </Alert>
        </Snackbar>
    );

    const renderReviewPage = () => (
        <Container maxWidth="xl" className={classes.ContainerRoot}>
            {renderSnackbar()}
            <Grid container direction="row" spacing={isMobile ? 2 : 4}>
                <Grid item xs={12} md={3}>
                    <Grid container direction="column" spacing={isMobile ? 0 : 2}>
                        <Grid item>
                            <Button
                                variant="text"
                                color="primary"
                                startIcon={<ChevronLeft />}
                                onClick={() =>
                                    history.push(
                                        `/timesheet/${timesheet.id}/edit?weekEnding=${timesheet.weekEnding}`
                                    )
                                }
                            >
                                <Typography variant="overline" className={classes.overline1}>
                                    {t('submitTimesheet:LINKS.GO_BACK_TO_EDIT_TIME')}
                                </Typography>
                            </Button>
                        </Grid>
                        <Grid item>
                            <Typography
                                variant="h3"
                                component="h3"
                                gutterBottom
                                className={classes.header}
                            >
                                {t('submitTimesheet:HEADERS.REVIEW_AND_SUBMIT')}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="body2" className={classes.bookingName}>
                                <SalesforceBookingLink
                                    bookingSfId={timesheet?.booking?.sfId}
                                    label={timesheet?.booking?.name}
                                />
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={9}>
                    <Grid container direction="column" spacing={4}>
                        {timesheetHasBeenSubmitted && (
                            <Grid item xs={12}>
                                <Alert severity="warning">
                                    {t('submitTimesheet:HEADERS.ALREADY_SUBMITTED_WARNING')}
                                    {formatListToCommaSeparated(locationsToBeSubmitted)}
                                </Alert>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Paper className={classes.PaperRoot}>
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    spacing={2}
                                >
                                    <Grid item>
                                        <Typography variant="h5" className={classes.header}>
                                            {formatPeriod(timesheet.weekEnding)}
                                        </Typography>
                                        <Chip
                                            label={
                                                timesheet.statusForDisplay ||
                                                TimesheetStatusEnum[timesheet.status] ||
                                                TimesheetStatusEnum.NotStarted
                                            }
                                            className={classes[`chip${timesheet.status}`]}
                                            classes={{ root: classes.chipRoot }}
                                            sx={{ border: '1px solid', marginTop: '8px' }}
                                        />
                                    </Grid>
                                    {ltoeWebTimesheetEnableExpectedPayDate && (
                                        <Grid item>
                                            <ExpectedPayDateCard timesheet={timesheet} />
                                        </Grid>
                                    )}
                                </Grid>
                                <Typography variant="body2" className={classes.bookingName}>
                                    <SalesforceBookingLink
                                        bookingSfId={timesheet?.booking?.sfId}
                                        label={timesheet?.booking?.name}
                                    />
                                </Typography>
                                {timesheet.booking.locations.map((location, index) =>
                                    renderTimesheetPanels(location, index)
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container justifyContent="flex-end">
                <Grid item>
                    <Button
                        onClick={debouncedOnSubmit}
                        variant="contained"
                        className={classes.containedSuccess}
                    >
                        {t('submitTimesheet:BUTTONS.SUBMIT')}
                    </Button>
                </Grid>
            </Grid>
        </Container>
    );

    return (
        <div className={classes.root}>
            <Prompt when={!isLoading && !timesheetHasBeenSubmitted} message={handlePrompt} />
            <RequiredQuestionDialog
                open={showUnsubmittedWarning}
                title={t('submitTimesheet:MESSAGES.UNSUBMITTED_TITLE')}
                text={t('submitTimesheet:MESSAGES.UNSUBMITTED_MESSAGE')}
                buttonTexts={t('submitTimesheet:MESSAGES.UNSUBMITTED_BUTTONS')}
                buttonIDs="Leave;Stay"
                buttonVariants="outlined;contained"
                preserveWhitespace={true}
                disableBackgroundClick={true}
                onClose={closeUnsubmittedWarning}
            />
            {isLoading ? (
                <CircularProgress classes={{ root: classes.progressRoot }} color="primary" />
            ) : (
                renderReviewPage()
            )}
        </div>
    );
}

// Export unwrapped component for testing
export { TimesheetReviewClinician };

export default WithContexts(withTranslation()(withStyles(styles)(TimesheetReviewClinician)));
